import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import type { Film } from "@/types/film";
import { TYPE_LABEL } from "@/types/film";

interface FilmCardProps {
  film: Film;
  highlight?: string;
}

function Highlight({ text, keyword }: { text: string; keyword?: string }) {
  const key = keyword?.trim().toLowerCase();
  if (!key) return <>{text}</>;

  const lower = text.toLowerCase();
  const parts: ReactNode[] = [];
  let start = 0;
  let found = lower.indexOf(key);

  while (found !== -1) {
    if (found > start) {
      parts.push(text.slice(start, found));
    }
    parts.push(
      <mark key={`${found}-${key}`} className="rounded-sm bg-accent-200 px-0.5 text-foreground-950">
        {text.slice(found, found + key.length)}
      </mark>,
    );
    start = found + key.length;
    found = lower.indexOf(key, start);
  }
  parts.push(text.slice(start));

  return <>{parts}</>;
}

export default function FilmCard({ film, highlight }: FilmCardProps) {
  return (
    <Link
      to={`/detail/${film.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
    >
      <div className="relative w-full overflow-hidden">
        <div className="aspect-[2/3] w-full overflow-hidden bg-background-200">
          <img
            src={film.cover}
            alt={`${film.title} 海报 - 新金瓶梅影视详情`}
            title={`${film.title} - 新金瓶梅影视资源与剧情介绍`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <span className="absolute left-2 top-2 rounded-md bg-primary-900/85 px-2 py-1 text-[11px] font-medium text-background-50">
          {TYPE_LABEL[film.type]}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-accent-500 px-2 py-1 text-[11px] font-bold text-primary-950">
          <i className="ri-star-fill" aria-hidden="true"></i>
          {film.rating.toFixed(1)}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-3.5">
        <h3 className="line-clamp-1 font-heading text-sm font-bold text-foreground-950 md:text-base">
          <Highlight text={film.title} keyword={highlight} />
        </h3>
        <p className="mt-1.5 line-clamp-1 text-xs text-foreground-600">
          {film.cast.slice(0, 3).join(" / ")}
        </p>
        <p className="mt-2 line-clamp-2 flex-1 text-xs leading-relaxed text-foreground-500">
          <Highlight text={film.summary} keyword={highlight} />
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {film.genres.slice(0, 3).map((genre) => (
            <span
              key={genre}
              className="rounded-md bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-900"
            >
              {genre}
            </span>
          ))}
          <span className="ml-auto text-[11px] text-foreground-400">{film.year}</span>
        </div>
      </div>
    </Link>
  );
}